import {
  derivePhases,
  runTicks,
  type HandPath,
  type ReplayTimeline,
} from './buildTimeline';
import { ensureFusionStreams } from './imuFusion';
import { computeTrajectories } from './trajectory';
import type { SensorSample, SessionManifest } from './types';

/**
 * The replay timeline built from a recording read as a STREAM
 * (SCALE-PORTAL-REPLAY-STREAMING, review P-22).
 *
 * `parseNdjson` + `buildTimeline` hold the whole file as one string and every
 * sample as an object. A two-hour recording is 1.1 GB of NDJSON and ~4.8 M
 * lines: past the browser's longest string, and over a gigabyte as objects.
 * This keeps each sample in compact typed columns (~190 MB for that file),
 * re-anchors skewed sensor clocks exactly as `buildTimeline` does, merges the
 * per-sensor runs (each already in time order) back into one time order, and
 * runs the same tick engine (`runTicks`), so the frames are `buildTimeline`'s.
 *
 * Two differences, both deliberate:
 *   - PPG lines are not kept: nothing in the replay reads them. They still
 *     count for the clock span, so durations match.
 *   - The 3D hand path needs every hand-sensor reading as an object (fusion,
 *     then double integration). Above `maxTrajectoryImuSamples` it is left
 *     out and `trajectorySkipped` says so; over hours the path is drift
 *     anyway (it is illustrative, not to scale).
 */

/** About half an hour of both hands at 100 Hz, accel + gyro. */
export const MAX_TRAJECTORY_IMU_SAMPLES = 800_000;

type TypedArray = Float64Array | Float32Array | Uint32Array | Uint16Array | Uint8Array;
type TypedCtor<T extends TypedArray> = { new (n: number): T };

/** A growable typed array. */
class Column<T extends TypedArray> {
  data: T;
  length = 0;

  constructor(private readonly Ctor: TypedCtor<T>, initial = 4096) {
    this.data = new Ctor(initial);
  }

  push(v: number): void {
    if (this.length === this.data.length) this.grow(this.length + 1);
    this.data[this.length++] = v;
  }

  private grow(min: number): void {
    let n = this.data.length * 2;
    while (n < min) n *= 2;
    const next = new this.Ctor(n);
    next.set(this.data);
    this.data = next;
  }
}

/** Index of `name` in `names`, added if new. */
function intern(names: string[], lookup: Map<string, number>, name: string): number {
  let i = lookup.get(name);
  if (i === undefined) {
    i = names.length;
    names.push(name);
    lookup.set(name, i);
  }
  return i;
}

const IMU_PREFIX = 'metawear-';

export class ReplayTimelineBuilder {
  private manifest: SessionManifest | null = null;
  private readonly minBy = new Map<string, number>();
  private readonly maxBy = new Map<string, number>();
  private readonly sensors: string[] = [];
  private readonly sensorIx = new Map<string, number>();
  private readonly streams: string[] = [];
  private readonly streamIx = new Map<string, number>();
  // Phase 0 = untagged.
  private readonly phases: (string | undefined)[] = [undefined];
  private readonly phaseIx = new Map<string, number>();
  private readonly time = new Column(Float64Array);
  private readonly device = new Column(Float64Array);
  private readonly sensor = new Column(Uint8Array);
  private readonly stream = new Column(Uint16Array);
  private readonly phase = new Column(Uint16Array);
  private readonly valStart = new Column(Uint32Array);
  private readonly values = new Column(Float32Array, 1 << 16);
  private imuCount = 0;

  constructor(private readonly maxTrajectoryImuSamples = MAX_TRAJECTORY_IMU_SAMPLES) {}

  /** Feed one NDJSON line; malformed lines are skipped, as in parseNdjson. */
  feedLine(raw: string): void {
    const line = raw.trim();
    if (line === '') return;
    let rec: Record<string, unknown>;
    try {
      const obj: unknown = JSON.parse(line);
      if (typeof obj !== 'object' || obj === null) return;
      rec = obj as Record<string, unknown>;
    } catch {
      return;
    }
    if (rec.kind === 'session') {
      this.manifest = rec as unknown as SessionManifest;
      return;
    }
    if (
      rec.kind !== 'sample' ||
      typeof rec.sensorId !== 'string' ||
      typeof rec.stream !== 'string' ||
      typeof rec.hostTime !== 'number' ||
      !Array.isArray(rec.values)
    ) {
      return;
    }
    const sensorId = rec.sensorId;
    const t = rec.hostTime;
    const mn = this.minBy.get(sensorId);
    if (mn === undefined || t < mn) this.minBy.set(sensorId, t);
    const mx = this.maxBy.get(sensorId);
    if (mx === undefined || t > mx) this.maxBy.set(sensorId, t);
    if (rec.stream.startsWith('ppg:')) return;

    this.time.push(t);
    this.device.push(typeof rec.deviceTime === 'number' ? rec.deviceTime : Number.NaN);
    this.sensor.push(intern(this.sensors, this.sensorIx, sensorId));
    this.stream.push(intern(this.streams, this.streamIx, rec.stream));
    this.phase.push(
      typeof rec.phase === 'string'
        ? intern(this.phases as string[], this.phaseIx, rec.phase)
        : 0,
    );
    this.valStart.push(this.values.length);
    for (const v of rec.values as unknown[]) {
      if (typeof v === 'number' && Number.isFinite(v)) this.values.push(v);
    }
    if (sensorId.startsWith(IMU_PREFIX)) this.imuCount++;
  }

  finish(): ReplayTimeline {
    const n = this.time.length;
    const empty: ReplayTimeline = {
      durationS: 0, bands: [], traits: [], accel: [], phases: [],
      trajectories: [], hasEeg: false, hasImu: false, hasTrajectory: false,
    };
    if (this.minBy.size === 0) return empty;

    // Same skew rule as buildTimeline.realignSensorClocks.
    let gMin = Infinity;
    let gMax = -Infinity;
    let widest = 0;
    for (const [id, mn] of this.minBy) {
      const mx = this.maxBy.get(id) ?? mn;
      gMin = Math.min(gMin, mn);
      gMax = Math.max(gMax, mx);
      widest = Math.max(widest, mx - mn);
    }
    const skewed = this.minBy.size > 1 && gMax - gMin > widest + 5000;
    const baseBySensor = this.sensors.map((id) => (skewed ? (this.minBy.get(id) ?? 0) : 0));
    const t0 = skewed ? 0 : gMin;
    const tEnd = skewed ? widest : gMax;
    const durationS = Math.max(0, (tEnd - t0) / 1000);
    if (n === 0) return { ...empty, durationS, phases: derivePhases(this.manifest, [], t0, durationS) };

    const times = this.time.data;
    const sensorOf = this.sensor.data;
    const at = (i: number) => times[i] - baseBySensor[sensorOf[i]];
    const order = this.timeOrder(n, at);

    const sampleAt = (i: number, withValues: boolean): SensorSample => {
      const end = i + 1 < n ? this.valStart.data[i + 1] : this.values.length;
      const dev = this.device.data[i];
      return {
        sensorId: this.sensors[sensorOf[i]],
        stream: this.streams[this.stream.data[i]],
        hostTime: at(i),
        deviceTime: Number.isNaN(dev) ? null : dev,
        values: withValues ? Array.from(this.values.data.subarray(this.valStart.data[i], end)) : [],
        phase: this.phases[this.phase.data[i]],
      };
    };

    // Phase transitions in time order (derivePhases' fallback when the
    // manifest has no phase segments only reacts to changes).
    const marks: SensorSample[] = [];
    let lastPhase = -1;
    for (let k = 0; k < n; k++) {
      const i = order[k];
      if (this.phase.data[i] !== lastPhase) {
        marks.push(sampleAt(i, false));
        lastPhase = this.phase.data[i];
      }
    }

    function* ordered(): Generator<SensorSample> {
      for (let k = 0; k < n; k++) yield sampleAt(order[k], true);
    }
    const ticks = runTicks(ordered(), t0, tEnd);

    let trajectories: HandPath[] = [];
    const trajectorySkipped = this.imuCount > this.maxTrajectoryImuSamples;
    if (this.imuCount > 0 && !trajectorySkipped) {
      const imu: SensorSample[] = [];
      for (let k = 0; k < n; k++) {
        const i = order[k];
        if (this.sensors[sensorOf[i]].startsWith(IMU_PREFIX)) imu.push(sampleAt(i, true));
      }
      trajectories = computeTrajectories(ensureFusionStreams(imu)).map((h) => ({
        sensorId: h.sensorId,
        points: h.points.map((p) => ({ t: (p.t - t0) / 1000, x: p.x, y: p.y, z: p.z })),
      }));
    }

    return {
      durationS,
      ...ticks,
      phases: derivePhases(this.manifest, marks, t0, durationS),
      trajectories,
      hasTrajectory: trajectories.some((h) => h.points.length > 1),
      ...(trajectorySkipped ? { trajectorySkipped: true } : {}),
    };
  }

  /** Sample indices in time order, ties in file order (what a stable sort
   *  by hostTime gives). Each sensor's samples normally arrive in order, so
   *  the per-sensor runs are merged; otherwise a full sort. */
  private timeOrder(n: number, at: (i: number) => number): Uint32Array {
    const runs: number[][] = this.sensors.map(() => []);
    for (let i = 0; i < n; i++) runs[this.sensor.data[i]].push(i);
    const inOrder = runs.every((r) => r.every((i, k) => k === 0 || at(i) >= at(r[k - 1])));
    const order = new Uint32Array(n);
    if (!inOrder) {
      const all = Array.from({ length: n }, (_, i) => i).sort((a, b) => at(a) - at(b) || a - b);
      order.set(all);
      return order;
    }
    const pos = runs.map(() => 0);
    for (let k = 0; k < n; k++) {
      let best = -1;
      for (let r = 0; r < runs.length; r++) {
        if (pos[r] >= runs[r].length) continue;
        if (best < 0) {
          best = r;
          continue;
        }
        const a = runs[r][pos[r]];
        const b = runs[best][pos[best]];
        if (at(a) < at(b) || (at(a) === at(b) && a < b)) best = r;
      }
      order[k] = runs[best][pos[best]++];
    }
    return order;
  }
}
